import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Form, InputGroup, Alert, Pagination } from 'react-bootstrap';
import axiosInstance from '../../api/axiosInstance';
import colors from '../../constants/colors';
// Import icons from react-icons
import { FaSearch } from 'react-icons/fa';
import ProductCard from '../../components/ProductCard/ProductCard';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('price_asc');
    
  // Define fetchProducts with useCallback
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // Prepare query params based on filters and sorting
      const sortParams = sortOption.split('_');
      const sortBy = sortParams[0];
      const sortOrder = sortParams[1];
      
      // Build query string
      let queryString = `?sortBy=${sortBy}&order=${sortOrder}&isDigital=false`;
      
    
      const response = await axiosInstance.get(`/product${queryString}`);
      
      if (response.rows) {
        const filteredProducts = response.rows.filter(product => searchTerm === '' || product.name.toLowerCase().includes(searchTerm.toLowerCase()));
        setProducts(filteredProducts);
        // Assume single page if pagination info is not provided
        setTotalPages(1);
      }
      
      setError('');
    } catch (err) {
      console.error("Error fetching products:", err);
      setError('Erro ao carregar produtos. Por favor tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [sortOption, searchTerm]);
  
  // Fetch products on component mount and when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
    fetchProducts();
  };
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    
    return (
      <Pagination className="justify-content-center my-4">
        <Pagination.Prev 
          onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
        />
        {items}
        <Pagination.Next 
          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  return (
    <Container className="py-5">
      <h1 className="mb-4">Produtos</h1>
      
      {/* Filters and Search */}
      <Row className="mb-4">
        <Col md={6}>
          <Form onSubmit={handleSearchSubmit}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Pesquisar produtos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button type="submit" style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}>
                <FaSearch />
              </Button>
            </InputGroup>
          </Form>
        </Col>
        <Col md={6}>
          <Form.Select 
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="price_asc">Preço: Menor para Maior</option>
            <option value="price_desc">Preço: Maior para Menor</option>
          </Form.Select>
        </Col>
      </Row>
      
      {/* Error Message */}
      {error && <Alert style={{ backgroundColor: colors.danger, color: colors.light, borderColor: colors.danger }}>{error}</Alert>}
      
      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center my-5">
          <Spinner style={{ color: colors.secondary }}
animation="border" role="status"/>
        </div>
      ) : (
        <>
          {/* Product Grid */}
          {products.length === 0 ? (
            <Alert style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}>
              Nenhum produto encontrado. Tente ajustar os filtros de pesquisa.
            </Alert>
          ) : (
            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
              {products.map((product) => (
                <Col key={product.id}>
                  <ProductCard product={product} />
                </Col>
              ))}
            </Row>
          )}
          
          {/* Pagination */}
          {renderPagination()}
        </>
      )}
    </Container>
  );
};

export default ProductsPage;
